import { selectContest, selectContests } from "./contest-selectors.js";
import { matchProgress } from "./match-progress.js";
import { adjustedContestTotal, playerBonusCount } from "./hole-bonus.js";
import { RoundResultError } from "../round/round-errors.js";
import { selectPlayerSummary } from "../round/round-selectors.js";
import type { RoundResult, RoundSnapshotV3 } from "../round/round-types.js";
import type { ContestSnapshot, ContestResultV3 } from "./contest-types.js";
import {
  importedTotalsProgress,
  importedRoundResult,
} from "../imported-round/imported-totals-result.js";

export interface ContestProgress {
  complete: boolean;
  result: ContestResultV3;
}

/** Pure progress/result calculation over the frozen agreement and recorded facts. */
export function selectContestProgress(
  snapshot: RoundSnapshotV3,
  contestId?: string,
): ContestProgress {
  const contest = selectContest(snapshot, contestId);
  if (contest.result) return { complete: true, result: contest.result };
  if (snapshot.importedTotals) return importedTotalsProgress(snapshot);
  if (contest.format === "match-play") return matchProgress(snapshot, contest);
  const summaries = contest.sides.map((side) =>
    selectPlayerSummary(snapshot, side.participantIds[0]!, contest.holeNumbers),
  );
  const stableford = contest.format === "stableford";
  const net = contest.scoringBasis === "net";
  const complete = summaries.every(
    (summary) =>
      summary.pendingHoles === 0 && (stableford || summary.pickupHoles === 0),
  );
  const sideTotals = competitionTotals(snapshot, contest, summaries);
  if (net && summaries.some((summary) => !summary.netReady))
    throw new RoundResultError("The agreed net handicap basis is missing.");
  const available = sideTotals
    .map((side) => side.value)
    .filter((value): value is number => value !== null);
  const best = available.length
    ? stableford
      ? Math.max(...available)
      : Math.min(...available)
    : null;
  const winners = complete
    ? sideTotals
        .filter((side) => side.value === best)
        .map((side) => side.sideId)
    : [];
  const sorted = [...available].sort((a, b) => (stableford ? b - a : a - b));
  const sameCoverage = summaries.every(
    (summary) => summary.completedHoles === summaries[0]!.completedHoles,
  );
  const leads =
    sameCoverage && best !== null
      ? sideTotals.filter((side) => side.value === best)
      : [];
  return {
    complete,
    result: {
      calculationVersion: "3",
      winnerSideIds: winners,
      tied: complete && winners.length > 1,
      sideTotals,
      unit: stableford ? "pts" : "strokes",
      holesPlayed: Math.min(
        ...summaries.map((summary) => summary.completedHoles),
      ),
      decidedAtHole: complete ? contest.holeNumbers.at(-1)! : null,
      leadSideId: leads.length === 1 ? leads[0]!.sideId : null,
      margin: sorted.length > 1 ? Math.abs(sorted[0]! - sorted[1]!) : 0,
      remainingHoles: Math.max(
        ...summaries.map((summary) => summary.pendingHoles),
      ),
      label: sideTotals.map((side) => side.value ?? "—").join("–"),
      dormie: false,
      decisionReason: complete ? "completed" : "unfinished",
    },
  };
}

export function calculateV3RoundResult(snapshot: RoundSnapshotV3): RoundResult {
  if (snapshot.importedTotals) return importedRoundResult(snapshot);
  const progress = selectContestProgress(snapshot);
  if (
    selectContests(snapshot).some(
      (contest) =>
        contest.adjudication?.decision !== "void" &&
        !selectContestProgress(snapshot, contest.id).complete,
    )
  )
    throw new RoundResultError(
      "The agreed contest is not complete. Record every required score or Stableford pickup.",
    );
  const contest = snapshot.mainContest;
  return {
    calculationVersion: "3",
    format: contest.format,
    winnerIds: contest.sides
      .filter((side) => progress.result.winnerSideIds.includes(side.id))
      .flatMap((side) => side.participantIds),
    tied: progress.result.tied,
    nassau: null,
    standings: contest.sides.map((side) => {
      const participantId = side.participantIds[0]!;
      const summary = selectPlayerSummary(
        snapshot,
        participantId,
        contest.holeNumbers,
      );
      const completeStrokeTotal =
        summary.pendingHoles === 0 && summary.pickupHoles === 0;
      return {
        targetId: participantId,
        gross: completeStrokeTotal ? summary.actualStrokes : null,
        net: completeStrokeTotal ? summary.netStrokes : null,
        stableford:
          contest.format === "stableford"
            ? contest.scoringBasis === "net"
              ? summary.netStableford
              : summary.grossStableford
            : null,
        holesWon:
          contest.format === "match-play"
            ? (progress.result.sideTotals.find(
                (total) => total.sideId === side.id,
              )?.value ?? 0) -
              playerBonusCount(
                snapshot,
                contest,
                participantId,
                contest.holeNumbers.slice(0, progress.result.holesPlayed),
              )
            : null,
        metric: null,
      };
    }),
  };
}

function competitionTotals(
  snapshot: RoundSnapshotV3,
  contest: ContestSnapshot,
  summaries: ReturnType<typeof selectPlayerSummary>[],
) {
  const stableford = contest.format === "stableford",
    net = contest.scoringBasis === "net";
  return contest.sides.map((side, index) => {
    const summary = summaries[index]!;
    return {
      sideId: side.id,
      value: adjustedContestTotal(
        stableford
          ? net
            ? summary.netStableford
            : summary.grossStableford
          : net
            ? summary.netStrokes
            : summary.actualStrokes,
        playerBonusCount(
          snapshot,
          contest,
          side.participantIds[0]!,
          contest.holeNumbers.filter(
            (number) =>
              snapshot.holeEntries[side.participantIds[0]!]?.[number]?.status &&
              snapshot.holeEntries[side.participantIds[0]!]![number]!.status !==
                "unset",
          ),
        ),
      ),
    };
  });
}
